import { type Address, type IToken, Token } from '@morpho-org/blue-sdk'
import { parseEther } from 'viem'

type ApiPrice = IToken['price'] | { usd: number } | null | undefined

const toPriceWad = (price: ApiPrice, priceUsd?: number | null): bigint | undefined => {
  if (price != null) {
    return typeof price === 'object' ? parseEther(price.usd.toFixed(18)) : BigInt(price)
  }
  return priceUsd == null ? undefined : parseEther(priceUsd.toFixed(18))
}

export interface InterfaceInputToken extends Omit<IToken, 'price'> {
  chain: {
    id: number
  }
  decimals: number
  logoURI: string | null
  price?: ApiPrice
}

export class InterfaceToken extends Token {
  public readonly chain: { id: number }
  constructor(
    token: InterfaceInputToken,
    readonly logoURI: string | null = token.logoURI
  ) {
    super({ ...token, price: toPriceWad(token.price) })
    this.chain = token.chain
  }
}

export interface VaultInput {
  /** Vault display name */
  name: string
  /** Vault address for linking to vault page */
  address: Address
  /** Underlying asset logo URI */
  underlyingAssetLogoURI: string | null
  /** Vault net APY */
  netApy: number | null
}

/**
 * Represents a vault reference for vault share tokens.
 * Contains the essential vault info needed for display and linking.
 */
export class Vault {
  public readonly name: string
  public readonly address: Address
  public readonly underlyingAssetLogoURI: string | null
  public readonly netApy: number | null

  constructor(input: VaultInput) {
    this.name = input.name
    this.address = input.address
    this.underlyingAssetLogoURI = input.underlyingAssetLogoURI
    this.netApy = input.netApy
  }
}

/**
 * Token with native yield (e.g., stETH, wstETH).
 * The yield is intrinsic to the token itself.
 */
export class YieldBearingToken extends InterfaceToken {
  // yield can't be declared as a parameter property because yield is a reserved word
  public readonly yield: number

  constructor(token: InterfaceInputToken, _yield: number) {
    super(token)
    this.yield = _yield
  }

  static tryGetApy(token: Token): number | null {
    if (token instanceof YieldBearingToken) return token.yield
    if (token instanceof VaultToken) return token.vault.netApy
    return null
  }
}

/**
 * Vault share token - the yield comes from the vault's performance.
 * The token represents shares in a vault.
 */
export class VaultToken extends InterfaceToken {
  public readonly vault: Vault

  constructor(token: InterfaceInputToken, vault: Vault) {
    super(token)
    this.vault = vault
  }
}

export type Amount = bigint | null

export interface TokenAmount {
  token: InterfaceInputToken
  amount: Amount
}

export type TokenAmountUsd = TokenAmount & {
  amountUsd: Amount
}

/**
 * Vault info from GraphQL API.
 * - name, address, asset: used by parseApiAsset to create Vault instance
 * - state.avgNetApy: the vault's average net APY
 * - id, __typename: common GraphQL fields that may be present but are not used
 */
export interface ApiVaultInfo {
  id?: string
  __typename?: string
  name?: string
  address?: string
  asset?: {
    id?: string
    __typename?: string
    logoURI?: string | null
  } | null
  state?: {
    avgNetApy?: number | null
  } | null
}

export type ApiInputToken = Omit<InterfaceInputToken, 'price'> & {
  chain: { id: number }
  yield?: { apy: number } | number | null
  price?: ApiPrice
  priceUsd?: number | null
  vault?: ApiVaultInfo | null
}

/**
 * Parses an API token into an InterfaceToken, YieldBearingToken, or VaultToken instance.
 * - If token has vault info with name/address → VaultToken
 * - If token has yield.apy (native yield) → YieldBearingToken
 * - Otherwise → InterfaceToken
 */
export function parseApiAsset(_token: ApiInputToken | InterfaceInputToken): InterfaceToken
export function parseApiAsset(
  _token: ApiInputToken | InterfaceInputToken | null | undefined
): InterfaceToken | null | undefined
export function parseApiAsset(
  _token: ApiInputToken | InterfaceInputToken | null | undefined
): InterfaceToken | null | undefined {
  if (!_token) return null

  const priceUsd = 'priceUsd' in _token ? _token.priceUsd : undefined
  const vaultInfo = 'vault' in _token ? _token.vault : undefined
  const price = toPriceWad(_token.price, priceUsd)

  // Vault share token - yield comes from vault's netApy
  if (vaultInfo?.name && vaultInfo?.address) {
    const vault = new Vault({
      name: vaultInfo.name,
      // oxlint-disable-next-line typescript/consistent-type-assertions
      address: vaultInfo.address as Address,
      underlyingAssetLogoURI: vaultInfo.asset?.logoURI ?? null,
      netApy: vaultInfo.state?.avgNetApy ?? null
    })
    return new VaultToken({ ..._token, price }, vault)
  }

  // Native yield token - yield is intrinsic to the token
  if ('yield' in _token && _token.yield != null) {
    const apy = typeof _token.yield === 'number' ? _token.yield : _token.yield.apy
    return new YieldBearingToken({ ..._token, price }, apy)
  }

  return new InterfaceToken({ ..._token, price }, _token.logoURI)
}
